/**
 * TarifasSection — zonas y tarifas versionadas de Correos (MVP-02C §3.5).
 *
 * Una tarifa activa no se edita: sus tramos solo se tocan mientras es
 * borrador (`is_active=False`). Activar una versión nueva desactiva la
 * anterior automáticamente; los pedidos ya creados conservan su propio
 * snapshot y no se ven afectados.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Plus, RotateCw, Trash2 } from "lucide-react";

import { TextInput, confirmDelete, showErrorAlert } from "../../../../../components/ui/index.js";
import {
    activateTariff, createTariff, createZone, fetchTariffs, fetchZones,
    parseAdminErrors, setZoneActive, updateTariffRates,
} from "../../../../../domain/shipping-admin/api.js";

const firstErrorMessage = (parsed) =>
    parsed.detail || parsed.general || Object.values(parsed)[0] || "No se pudo completar la operación.";

const Campo = ({ id, label, children, suffix }) => (
    <div className="form-control gap-1">
        <label className="label py-0" htmlFor={id}>
            <span className="label-text text-xs font-semibold">{label}{suffix && ` (${suffix})`}</span>
        </label>
        {children}
    </div>
);
Campo.propTypes = { id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, children: PropTypes.node, suffix: PropTypes.string };

function ZonesPanel({ zones, onChanged }) {
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    const toggleZone = async (zone) => {
        const activating = !zone.is_active;
        const ok = await confirmDelete({
            title: activating ? `¿Activar la zona «${zone.name}»?` : `¿Desactivar la zona «${zone.name}»?`,
            text: activating
                ? "El cotizador podrá usar esta zona para tarifas nuevas."
                : "Ninguna tarifa podrá usar esta zona hasta que se reactive.",
            confirmButtonText: activating ? "Activar" : "Desactivar",
        });
        if (!ok) return;

        setTogglingId(zone.id);
        try {
            await setZoneActive(zone.id, activating);
            onChanged();
        } catch (err) {
            showErrorAlert({ text: firstErrorMessage(parseAdminErrors(err)) });
        } finally {
            setTogglingId(null);
        }
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            await createZone({ code, name, is_active: true });
            setCode("");
            setName("");
            onChanged();
        } catch (err) {
            setError(parseAdminErrors(err).general || parseAdminErrors(err).code || "No se pudo crear la zona.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="flex flex-col gap-3">
            <h3 className="font-semibold text-sm">Zonas</h3>
            {zones.length === 0 ? (
                <p className="text-sm text-base-content/50">
                    Sin zonas todavía. El MVP solo cubre España peninsular: crea una con
                    código «peninsula».
                </p>
            ) : (
                <ul className="flex flex-wrap gap-2">
                    {zones.map((zone) => (
                        <li key={zone.id}>
                            <button
                                type="button"
                                onClick={() => toggleZone(zone)}
                                disabled={togglingId === zone.id}
                                className={`badge gap-1.5 cursor-pointer ${zone.is_active ? "badge-success" : "badge-ghost"}`}
                            >
                                {togglingId === zone.id ? "…" : `${zone.name} (${zone.code})`}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
                <Campo id="zone-code" label="Código">
                    <TextInput id="zone-code" value={code} onChange={(e) => setCode(e.target.value)}
                        placeholder="peninsula" inputSize="sm" />
                </Campo>
                <Campo id="zone-name" label="Nombre">
                    <TextInput id="zone-name" value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="Península" inputSize="sm" />
                </Campo>
                <button type="submit" className="btn btn-sm btn-outline" disabled={saving || !code || !name}>
                    Añadir zona
                </button>
            </form>
            {error && <p role="alert" className="text-sm text-error">{error}</p>}
        </section>
    );
}
ZonesPanel.propTypes = { zones: PropTypes.array.isRequired, onChanged: PropTypes.func.isRequired };

function RatesEditor({ zones, rates, onChange }) {
    const setRate = (index, field, value) =>
        onChange(rates.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    const addRate = () =>
        onChange([...rates, { zone: zones[0]?.id ?? "", weight_from_grams: 0, weight_to_grams: 1000, price: "0.00", surcharge: "0.00" }]);
    const removeRate = (index) => onChange(rates.filter((_, i) => i !== index));

    return (
        <div className="flex flex-col gap-2">
            {rates.map((rate, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                    <Campo id={`rate-zone-${index}`} label="Zona">
                        <select
                            id={`rate-zone-${index}`}
                            className="select select-bordered select-sm"
                            value={rate.zone}
                            onChange={(e) => setRate(index, "zone", Number(e.target.value))}
                        >
                            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                    </Campo>
                    <Campo id={`rate-from-${index}`} label="Desde" suffix="g">
                        <TextInput id={`rate-from-${index}`} type="number" min="0" inputSize="sm"
                            value={rate.weight_from_grams}
                            onChange={(e) => setRate(index, "weight_from_grams", Number(e.target.value))} />
                    </Campo>
                    <Campo id={`rate-to-${index}`} label="Hasta" suffix="g">
                        <TextInput id={`rate-to-${index}`} type="number" min="0" inputSize="sm"
                            value={rate.weight_to_grams}
                            onChange={(e) => setRate(index, "weight_to_grams", Number(e.target.value))} />
                    </Campo>
                    <Campo id={`rate-price-${index}`} label="Precio" suffix="EUR, sin IVA">
                        <TextInput id={`rate-price-${index}`} type="number" min="0" step="0.01" inputSize="sm"
                            value={rate.price} onChange={(e) => setRate(index, "price", e.target.value)} />
                    </Campo>
                    <div className="flex items-end gap-1">
                        <Campo id={`rate-sur-${index}`} label="Suplemento" suffix="EUR">
                            <TextInput id={`rate-sur-${index}`} type="number" min="0" step="0.01" inputSize="sm"
                                value={rate.surcharge} onChange={(e) => setRate(index, "surcharge", e.target.value)} />
                        </Campo>
                        <button type="button" onClick={() => removeRate(index)} className="btn btn-ghost btn-xs btn-square" aria-label="Quitar tramo">
                            <Trash2 className="w-3.5 h-3.5 text-error" />
                        </button>
                    </div>
                </div>
            ))}
            <button type="button" onClick={addRate} className="btn btn-ghost btn-xs gap-1 self-start" disabled={zones.length === 0}>
                <Plus className="w-3 h-3" /> Añadir tramo
            </button>
        </div>
    );
}
RatesEditor.propTypes = { zones: PropTypes.array.isRequired, rates: PropTypes.array.isRequired, onChange: PropTypes.func.isRequired };

const TARIFF_FORM_VACIO = {
    carrier: "correos", service_code: "estandar", modality: "domicilio",
    currency: "EUR", tax_rate: "0.21", volumetric_divisor: 5000, source: "",
};

function NewTariffForm({ zones, onCreated, onCancel }) {
    const [header, setHeader] = useState(TARIFF_FORM_VACIO);
    const [rates, setRates] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setErrors({});
        try {
            await createTariff({ ...header, rates });
            setHeader(TARIFF_FORM_VACIO);
            setRates([]);
            onCreated();
        } catch (error) {
            setErrors(parseAdminErrors(error));
        } finally {
            setSaving(false);
        }
    };

    const fieldErrors = Object.entries(errors).filter(
        ([k]) => k !== "general" && k !== "rates",
    );

    return (
        <form onSubmit={submit} className="rounded-xl border border-base-300 bg-base-200 p-4 flex flex-col gap-4">
            <h3 className="font-semibold text-sm">Nueva tarifa (versión borrador)</h3>
            {errors.general && (
                <p role="alert" className="text-sm text-error">{errors.general}</p>
            )}
            {typeof errors.rates === "string" && (
                <p role="alert" className="text-sm text-error">Tramos: {errors.rates}</p>
            )}
            {fieldErrors.length > 0 && (
                <ul role="alert" className="text-sm text-error list-disc list-inside">
                    {fieldErrors.map(([k, v]) => <li key={k}>{k}: {v}</li>)}
                </ul>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Campo id="tariff-source" label="Fuente / referencia">
                    <TextInput id="tariff-source" value={header.source}
                        onChange={(e) => setHeader((h) => ({ ...h, source: e.target.value }))}
                        placeholder="Correos Paq Estándar 2026" />
                </Campo>
                <Campo id="tariff-currency" label="Moneda">
                    <TextInput id="tariff-currency" value={header.currency}
                        onChange={(e) => setHeader((h) => ({ ...h, currency: e.target.value }))} />
                </Campo>
                <Campo id="tariff-tax" label="IVA" suffix="ej. 0.21 = 21%">
                    <TextInput id="tariff-tax" type="number" step="0.0001" min="0" value={header.tax_rate}
                        onChange={(e) => setHeader((h) => ({ ...h, tax_rate: e.target.value }))} />
                </Campo>
                <Campo id="tariff-vol" label="Divisor volumétrico" suffix="cm³/kg">
                    <TextInput id="tariff-vol" type="number" min="1" value={header.volumetric_divisor}
                        onChange={(e) => setHeader((h) => ({ ...h, volumetric_divisor: Number(e.target.value) }))} />
                </Campo>
            </div>

            <div>
                <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold mb-2">
                    Tramos de peso facturable
                </p>
                {zones.length === 0 ? (
                    <p className="text-sm text-warning">Crea al menos una zona activa antes de añadir tramos.</p>
                ) : (
                    <RatesEditor zones={zones} rates={rates} onChange={setRates} />
                )}
            </div>

            <div className="flex gap-2">
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving || rates.length === 0}>
                    {saving ? "Guardando…" : "Guardar como borrador"}
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancelar</button>
            </div>
        </form>
    );
}
NewTariffForm.propTypes = { zones: PropTypes.array.isRequired, onCreated: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

function DraftTariffRow({ tariff, zones, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [rates, setRates] = useState(
        tariff.rates.map((r) => ({ ...r, zone: r.zone?.id ?? r.zone })),
    );
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const saveRates = async () => {
        setBusy(true);
        setError("");
        try {
            await updateTariffRates(tariff.id, rates);
            setEditing(false);
            onChanged();
        } catch (err) {
            const parsed = parseAdminErrors(err);
            setError(parsed.rates || parsed.general || "No se pudo guardar.");
        } finally {
            setBusy(false);
        }
    };

    const activate = async () => {
        const ok = await confirmDelete({
            title: `¿Activar la tarifa v${tariff.version}?`,
            text: "Sustituirá de inmediato a la tarifa actualmente activa para nuevas cotizaciones. Los pedidos ya pagados conservan su propio precio congelado.",
            confirmButtonText: "Activar",
        });
        if (!ok) return;

        setBusy(true);
        setError("");
        try {
            await activateTariff(tariff.id);
            onChanged();
        } catch (err) {
            const parsed = parseAdminErrors(err);
            setError(parsed.detail || parsed.general || "No se pudo activar.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <tr>
            <td colSpan={6} className="p-0">
                <div className="p-3 flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm">
                            <span className="font-semibold">v{tariff.version}</span> · {tariff.source || "sin referencia"} ·{" "}
                            {tariff.rates.length} tramo(s)
                        </div>
                        <div className="flex gap-2">
                            <button type="button" className="btn btn-xs btn-outline" onClick={() => setEditing((v) => !v)}>
                                {editing ? "Cerrar" : "Editar tramos"}
                            </button>
                            <button type="button" className="btn btn-xs btn-success" disabled={busy} onClick={activate}>
                                {busy ? "…" : "Activar"}
                            </button>
                        </div>
                    </div>
                    {editing && (
                        <>
                            <RatesEditor zones={zones} rates={rates} onChange={setRates} />
                            <button type="button" className="btn btn-xs btn-primary self-start" disabled={busy} onClick={saveRates}>
                                Guardar tramos
                            </button>
                        </>
                    )}
                    {error && <p role="alert" className="text-sm text-error">{error}</p>}
                </div>
            </td>
        </tr>
    );
}
DraftTariffRow.propTypes = { tariff: PropTypes.object.isRequired, zones: PropTypes.array.isRequired, onChanged: PropTypes.func.isRequired };

export default function TarifasSection({ onChanged }) {
    const [tariffs, setTariffs] = useState({ state: "loading", data: [] });
    const [zones, setZones] = useState([]);
    const [creating, setCreating] = useState(false);

    const cargar = useCallback(() => {
        setTariffs((prev) => ({ ...prev, state: "loading" }));
        return Promise.all([fetchTariffs(), fetchZones()])
            .then(([t, z]) => {
                setTariffs({ state: "ready", data: t });
                setZones(z);
            })
            .catch(() => setTariffs({ state: "error", data: [] }));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleChanged = () => {
        setCreating(false);
        cargar();
        onChanged();
    };

    if (tariffs.state === "loading") {
        return (
            <div className="flex flex-col gap-2" aria-busy="true">
                <div className="h-20 rounded-lg bg-base-300/60 animate-pulse" />
                <span className="sr-only">Cargando tarifas…</span>
            </div>
        );
    }
    if (tariffs.state === "error") {
        return (
            <div role="alert" className="flex flex-col items-start gap-3 py-6">
                <p className="text-sm text-error">No se pudieron cargar las tarifas.</p>
                <button type="button" className="btn btn-sm gap-1" onClick={cargar}>
                    <RotateCw className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    const activeZones = zones.filter((z) => z.is_active);

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-6">
            <ZonesPanel zones={zones} onChanged={cargar} />

            <section className="flex flex-col gap-3 border-t border-base-300 pt-4">
                <h3 className="font-semibold text-sm">Tarifas (correos / estándar)</h3>
                {tariffs.data.length === 0 ? (
                    <p className="text-sm text-base-content/50">Todavía no hay ninguna tarifa.</p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-base-300">
                        <table className="table table-sm table-zebra">
                            <thead>
                                <tr><th>Versión</th><th>Fuente</th><th>Tramos</th><th>Estado</th></tr>
                            </thead>
                            <tbody>
                                {tariffs.data.map((tariff) => (
                                    tariff.is_active ? (
                                        <tr key={tariff.id}>
                                            <td>v{tariff.version}</td>
                                            <td className="text-xs">{tariff.source || "—"}</td>
                                            <td className="text-xs">{tariff.rates.length}</td>
                                            <td><span className="badge badge-success badge-sm">Activa</span></td>
                                        </tr>
                                    ) : (
                                        <DraftTariffRow key={tariff.id} tariff={tariff} zones={activeZones} onChanged={handleChanged} />
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {creating ? (
                    <NewTariffForm zones={activeZones} onCreated={handleChanged} onCancel={() => setCreating(false)} />
                ) : (
                    <button type="button" onClick={() => setCreating(true)} className="btn btn-outline btn-sm gap-1 self-start">
                        <Plus className="w-4 h-4" /> Nueva tarifa
                    </button>
                )}
            </section>
        </div>
    );
}

TarifasSection.propTypes = { onChanged: PropTypes.func.isRequired };
